import { Affix, Box, Notification, Text, Transition } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import useDocumentTitle from "~/hooks/useDocumentTitle";
import {
  DEFAULT_MESSAGE,
  DEFAULT_SPACING,
  DEFAULT_SPACING_MOBILE,
} from "~/utils/Constants";
import { SnackbarContext } from "~/utils/Contexts";
import { Message } from "~/utils/Types";

import Center from "./Center";
import Link from "./Link";
import NavBar from "./Nav";
import SuspenseWrapper from "./SuspenseWrapper";

import classes from "./PageContainer.module.css";

export const Footer = () => {
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery("(max-width: 37.5em)", false, {
    getInitialValueInEffect: false,
  });
  const textSize = isSmallScreen ? "xs" : "sm";

  return (
    <Center
      direction="row"
      c="var(--mantine-color-text-disabled)"
      spacing={isSmallScreen ? 0.2 : 1}
      mt={{ base: DEFAULT_SPACING_MOBILE * 8, md: DEFAULT_SPACING * 8 }}
    >
      <Text size={textSize}>
        {t("footer.copyright", { year: new Date().getFullYear() })}
      </Text>
      <Text size={textSize}>|</Text>
      <Link
        href="files/Terms_of_Service_070824.pdf"
        target="_blank"
        fz={textSize}
        className={classes.footerLink}
      >
        {t("footer.termsAndConditions")}
      </Link>
      <Text size={textSize}>|</Text>
      <Link
        href="files/Privacy_Policy_070824.pdf"
        target="_blank"
        fz={textSize}
        className={classes.footerLink}
      >
        {t("footer.privacyPolicy")}
      </Link>
    </Center>
  );
};

// MUI Alert severity -> Mantine color for the snackbar notification.
const SNACKBAR_COLORS: Record<string, string> = {
  success: "green",
  error: "red",
  info: "blue",
  warning: "yellow",
};

const SNACKBAR_AUTO_HIDE_MS = 5000;

interface PageProps {
  title: string;
  children?: ReactNode;
  hideTitle?: boolean;
  hideNav?: boolean;
}

const PageContainer = ({ title, children, hideTitle, hideNav }: PageProps) => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);

  useDocumentTitle(title);

  const snackbar = useMemo(
    () => ({
      open: (m: Message) => {
        setMessage(m);
        setOpenSnackbar(true);
      },
      close: () => setOpenSnackbar(false),
    }),
    [],
  );

  useEffect(() => {
    if (!openSnackbar) return;
    const timer = setTimeout(
      () => setOpenSnackbar(false),
      SNACKBAR_AUTO_HIDE_MS,
    );
    return () => clearTimeout(timer);
  }, [openSnackbar, message]);

  return (
    <SnackbarContext.Provider value={snackbar}>
      <Box h="100vh">
        {hideNav ? (
          <Box h="100%">
            <SuspenseWrapper>{children}</SuspenseWrapper>
          </Box>
        ) : (
          <NavBar title={hideTitle ? undefined : title}>
            <SuspenseWrapper>{children}</SuspenseWrapper>
          </NavBar>
        )}
        {message?.text && (
          <Affix position={{ bottom: 24, left: 24 }}>
            <Transition transition="slide-right" mounted={openSnackbar}>
              {(transitionStyle) => (
                <Notification
                  color={SNACKBAR_COLORS[message.type ?? "info"] ?? "blue"}
                  onClose={() => setOpenSnackbar(false)}
                  withCloseButton
                  withBorder
                  style={transitionStyle}
                >
                  {message.text}
                </Notification>
              )}
            </Transition>
          </Affix>
        )}
      </Box>
    </SnackbarContext.Provider>
  );
};

export default PageContainer;
